if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        
        // Use capture phase to catch submission early
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this), true);
        
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        // Check if this is a glasses product - check both the submit button and any button with data-is-glasses
        const glassesButton = this.querySelector('[data-is-glasses="true"]') || document.querySelector(`[form="${this.form.id}"][data-is-glasses="true"]`);
        this.isGlassesProduct = glassesButton?.dataset.isGlasses === 'true' || this.submitButton?.dataset.isGlasses === 'true';
        
        // Also check if lens-selector exists on the page (more reliable indicator)
        if (!this.isGlassesProduct) {
          const lensSelectorExists = document.querySelector('lens-selector');
          if (lensSelectorExists) {
            this.isGlassesProduct = true;
          }
        }
        
        this.selectedLens = null;
        this.prescriptionData = null;
        this.prescriptionMethod = null;
        this.lensSelector = null;
        this.variantChangeUnsubscriber = null;

        this.initLensSelector();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        // Clean up lens selection event listener
        if (this.lensSelectedHandler) {
          document.removeEventListener('lens-selected', this.lensSelectedHandler);
          if (this.lensSelector) {
            this.lensSelector.removeEventListener('lens-selected', this.lensSelectedHandler);
          }
        }
        // Clean up MutationObserver
        if (this.lensSelectorObserver) {
          this.lensSelectorObserver.disconnect();
        }
      }

      initLensSelector() {
        if (!this.isGlassesProduct) {
          console.log('Product-form: Not a glasses product, skipping lens selector init');
          return;
        }

        console.log('Product-form: Initializing lens selector...');

        // Find lens selector component - search more broadly
        this.lensSelector = document.querySelector('lens-selector');
        
        if (!this.lensSelector) {
          // Try finding it relative to the form
          this.lensSelector = this.closest('.product-form')?.parentElement?.querySelector('lens-selector');
        }
        
        if (!this.lensSelector) {
          // Try finding it in the product info container
          const productInfo = document.querySelector('product-info');
          if (productInfo) {
            this.lensSelector = productInfo.querySelector('lens-selector');
          }
        }
        
        console.log('Product-form: Lens selector found:', !!this.lensSelector, this.lensSelector);

        // Set up lens selection handler - use document-level listener for reliability
        // This works even if lens-selector is moved or found later
        this.lensSelectedHandler = (e) => {
          console.log('Product-form: Lens selected event received:', e.detail);
          // Accept any lens-selected event - we'll update buttons regardless
          // The event detail contains the lens information
          if (e.detail && e.detail.lens) {
            console.log('Product-form: Setting selectedLens:', e.detail.lens);
            this.selectedLens = e.detail.lens;
            this.prescriptionData = e.detail.prescriptionData || null;
            this.prescriptionMethod = e.detail.prescriptionMethod || null;
            window.lensSelected = true; // Set global flag
            
            // Force update button state immediately
            this.updateSubmitButton();
            
            // Also trigger updates with delays to ensure DOM is ready
            setTimeout(() => {
              this.updateSubmitButton();
            }, 50);
            setTimeout(() => {
              this.updateSubmitButton();
            }, 200);
          }
        };
        
        // Listen on document level (event bubbles) - this is the primary listener
        document.addEventListener('lens-selected', this.lensSelectedHandler);
        console.log('Product-form: Set up lens-selected event listener on document');
        
        if (this.lensSelector) {
          // Also listen directly on lens-selector as backup listener
          this.lensSelector.addEventListener('lens-selected', this.lensSelectedHandler);
          console.log('Product-form: Also listening on lens-selector element');

          // Check if lens is already selected
          if (this.lensSelector.dataset.selectedLensId) {
            this.selectedLens = {
              variantId: this.lensSelector.dataset.selectedLensId,
              title: this.lensSelector.dataset.selectedLensTitle
            };
            window.lensSelected = true;
            this.updateSubmitButton();
          }
          
          // Also set up a MutationObserver to watch for dataset changes on lens-selector
          // This is a fallback in case events don't fire
          this.lensSelectorObserver = new MutationObserver(() => {
            if (this.lensSelector.dataset.selectedLensId && !this.selectedLens) {
              this.selectedLens = {
                variantId: this.lensSelector.dataset.selectedLensId,
                title: this.lensSelector.dataset.selectedLensTitle
              };
              window.lensSelected = true;
              this.updateSubmitButton();
            } else if (!this.lensSelector.dataset.selectedLensId && this.selectedLens) {
              this.selectedLens = null;
              window.lensSelected = false;
              this.updateSubmitButton();
            }
          });
          
          this.lensSelectorObserver.observe(this.lensSelector, {
            attributes: true,
            attributeFilter: ['data-selected-lens-id', 'data-selected-lens-title']
          });
        }

        // Override toggleAddButton to respect lens selection
        this.overrideToggleAddButton();

        // Listen for variant changes to keep button disabled
        this.subscribeToVariantChanges();

        // Update all submit buttons (including sticky cart)
        this.updateSubmitButton();
      }

      subscribeToVariantChanges() {
        // Listen for variant change events
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (this.isGlassesProduct && !this.selectedLens) {
              // Re-disable button after variant change
              setTimeout(() => {
                this.updateSubmitButton();
              }, 100);
            }
          });
        }
        
        // Buttons are now always enabled, so no need to watch for disabled attribute changes
      }

      overrideToggleAddButton() {
        // Buttons are now always enabled, so no need to override toggleAddButton
      }

      updateSubmitButton() {
        // Always try to update buttons if we have a selected lens or if lens-selector exists
        const hasLensSelector = document.querySelector('lens-selector') !== null;
        const hasSelectedLens = this.selectedLens !== null;
        
        if (!this.isGlassesProduct && !hasLensSelector && !hasSelectedLens) {
          return;
        }

        // Update all submit buttons (main and sticky cart)
        // Try multiple selectors to catch all possible button locations
        const selectors = [
          '[name="add"][data-is-glasses="true"]',
          'button[type="submit"][data-is-glasses="true"]',
          `#ProductSubmitButton-${this.dataset.sectionId}[data-is-glasses="true"]`,
          `#ProductSubmitButton-${this.dataset.sectionId}`
        ];
        
        const allSubmitButtons = new Set();
        selectors.forEach(selector => {
          try {
            document.querySelectorAll(selector).forEach(btn => {
              // Only include buttons that are actually for glasses products
              if (btn.dataset.isGlasses === 'true' || btn.classList.contains('lens-required')) {
                allSubmitButtons.add(btn);
              }
            });
          } catch (e) {
            // Ignore invalid selectors
          }
        });
        
        // Also check buttons in this form
        if (this.form) {
          const formButtons = this.form.querySelectorAll('[name="add"]');
          formButtons.forEach(btn => {
            if (btn.dataset.isGlasses === 'true' || btn.classList.contains('lens-required')) {
              allSubmitButtons.add(btn);
            }
          });
        }
        
        // Also check the submitButton property directly
        if (this.submitButton && (this.submitButton.dataset.isGlasses === 'true' || this.submitButton.classList.contains('lens-required'))) {
          allSubmitButtons.add(this.submitButton);
        }
        
        allSubmitButtons.forEach(button => {
          // Always keep buttons enabled - remove disabled attribute
          button.removeAttribute('disabled');
          button.disabled = false;
          
          if (this.selectedLens || window.lensSelected) {
            button.classList.remove('lens-required');
            // Set data attribute to indicate lens is selected
            button.dataset.lensSelected = 'true';
            // Remove onclick handler since lens is selected
            button.removeAttribute('onclick');
            const span = button.querySelector('span');
            if (span) {
              span.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          } else {
            // Keep button enabled but maintain lens-required class for styling if needed
            button.classList.add('lens-required');
            // Remove data attribute
            delete button.dataset.lensSelected;
            // Remove onclick handler to allow normal submission
            button.removeAttribute('onclick');
            const span = button.querySelector('span');
            if (span) {
              span.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          }
        });
      }

      onSubmitHandler(evt) {
        // CRITICAL: Check FIRST - check if lens-selector exists on page
        const lensSelectorOnPage = document.querySelector('lens-selector');
        const isGlassesProductCheck = lensSelectorOnPage !== null || this.isGlassesProduct;
        
        // Re-check if this is a glasses product (in case detection failed)
        if (!this.isGlassesProduct && lensSelectorOnPage) {
          this.isGlassesProduct = true;
          this.initLensSelector();
        }
        
        // Get the actual button that was clicked (might be sticky cart button)
        const clickedButton = evt.submitter || this.submitButton;
        const isGlassesBtn = clickedButton?.dataset.isGlasses === 'true' || isGlassesProductCheck;
        
        // CRITICAL: Check if glasses product requires lens selection - block ALL submissions
        // Always try to sync lens data from lens-selector (multiple sources)
        if (isGlassesProductCheck) {
          const lensSelector = this.lensSelector || document.querySelector('lens-selector');
          
          // Try multiple ways to get lens data
          if (lensSelector) {
            // Method 1: Use getSelectedLens() method
            if (!this.selectedLens && typeof lensSelector.getSelectedLens === 'function') {
              this.selectedLens = lensSelector.getSelectedLens();
            }
            
            // Method 2: Check dataset attributes
            if (!this.selectedLens && lensSelector.dataset.selectedLensId) {
              this.selectedLens = {
                variantId: lensSelector.dataset.selectedLensId,
                title: lensSelector.dataset.selectedLensTitle
              };
            }
            
            // Method 3: Check if lens-selector has selectedLens property directly
            if (!this.selectedLens && lensSelector.selectedLens) {
              this.selectedLens = lensSelector.selectedLens;
            }
            
            // Also sync prescription data if available
            if (lensSelector.prescriptionData) {
              this.prescriptionData = lensSelector.prescriptionData;
            }
            if (lensSelector.prescriptionMethod) {
              this.prescriptionMethod = lensSelector.prescriptionMethod;
            }
          }
          
          // Method 4: Check window.lensSelected flag
          if (!this.selectedLens && window.lensSelected) {
            // If flag is set but we don't have the lens object, try to get it again
            if (lensSelector) {
              if (typeof lensSelector.getSelectedLens === 'function') {
                this.selectedLens = lensSelector.getSelectedLens();
              } else if (lensSelector.dataset.selectedLensId) {
                this.selectedLens = {
                  variantId: lensSelector.dataset.selectedLensId,
                  title: lensSelector.dataset.selectedLensTitle
                };
              }
            }
          }
        }
        
        // Final check: Try to get lens from lens-selector one more time right before the check
        if (isGlassesProductCheck && !this.selectedLens) {
          const finalLensSelector = this.lensSelector || document.querySelector('lens-selector');
          if (finalLensSelector) {
            // Try getSelectedLens method
            if (typeof finalLensSelector.getSelectedLens === 'function') {
              const lens = finalLensSelector.getSelectedLens();
              if (lens) this.selectedLens = lens;
            }
            // Try dataset
            if (!this.selectedLens && finalLensSelector.dataset.selectedLensId) {
              this.selectedLens = {
                variantId: finalLensSelector.dataset.selectedLensId,
                title: finalLensSelector.dataset.selectedLensTitle
              };
            }
            // Try direct property access
            if (!this.selectedLens && finalLensSelector.selectedLens) {
              this.selectedLens = finalLensSelector.selectedLens;
            }
          }
        }
        
        // Check multiple sources for lens selection status
        const lensSelected = this.selectedLens || window.lensSelected || clickedButton?.dataset.lensSelected === 'true';
        
        // Buttons are now always enabled, so we allow submission even without lens selection
        // Lens selection is optional - if selected, it will be included in the cart
        
        // Prevent default for all submissions (we'll handle it manually)
        evt.preventDefault();
        
        if (clickedButton && clickedButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // If glasses product, add both glass and lens
        // Final sync attempt if still no lens
        if (this.isGlassesProduct) {
          if (!this.selectedLens && window.lensSelected) {
            const lensSelector = this.lensSelector || document.querySelector('lens-selector');
            if (lensSelector) {
              if (typeof lensSelector.getSelectedLens === 'function') {
                this.selectedLens = lensSelector.getSelectedLens();
              } else if (lensSelector.dataset.selectedLensId) {
                this.selectedLens = {
                  variantId: lensSelector.dataset.selectedLensId,
                  title: lensSelector.dataset.selectedLensTitle
                };
              }
              // Also sync prescription data
              if (lensSelector.prescriptionData) {
                this.prescriptionData = lensSelector.prescriptionData;
              }
              if (lensSelector.prescriptionMethod) {
                this.prescriptionMethod = lensSelector.prescriptionMethod;
              }
            }
          }
          
          if (this.selectedLens && this.selectedLens.variantId) {
            // Lens is selected, add both glasses and lens
            this.addGlassesWithLens();
            return;
          } else {
            // Lens not selected - just add the product to cart normally
            // Continue with normal add to cart flow below
          }
        }

        // Normal product add to cart
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      async addGlassesWithLens() {
        // Ensure we have the lens data
        if (!this.selectedLens || !this.selectedLens.variantId) {
          console.error('addGlassesWithLens: No lens selected', this.selectedLens);
          this.handleErrorMessage('Lens selection is missing. Please select a lens again.');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }
        
        const glassVariantId = this.form.querySelector('[name="id"]').value;
        const quantity = this.form.querySelector('[name="quantity"]')?.value || 1;
        const lensVariantId = this.selectedLens.variantId;

        // Generate pair ID
        const pairId = `glass_${glassVariantId}_lens_${lensVariantId}`;

        try {
          // Add glass first
          const glassFormData = new FormData();
          glassFormData.append('id', glassVariantId);
          glassFormData.append('quantity', quantity);
          glassFormData.append('properties[_pair_id]', pairId);
          glassFormData.append('properties[_type]', 'glass');

          if (this.cart) {
            glassFormData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            glassFormData.append('sections_url', window.location.pathname);
          }

          const glassConfig = fetchConfig('javascript');
          glassConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete glassConfig.headers['Content-Type'];
          glassConfig.body = glassFormData;

          const glassResponse = await fetch(`${routes.cart_add_url}`, glassConfig);
          const glassResult = await glassResponse.json();

          if (glassResult.status) {
            this.handleErrorMessage(glassResult.description || 'Error adding glasses to cart');
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            return;
          }

          // Add lens second
          const lensFormData = new FormData();
          lensFormData.append('id', lensVariantId);
          lensFormData.append('quantity', quantity);
          lensFormData.append('properties[_pair_id]', pairId);
          lensFormData.append('properties[_type]', 'lens');
          
          // Add prescription data if available
          if (this.prescriptionData) {
            lensFormData.append('properties[Prescription Method]', this.prescriptionMethod === 'upload' ? 'Upload Prescription' : 'Enter Prescription');
            
            if (this.prescriptionData.od) {
              lensFormData.append('properties[OD SPH]', this.prescriptionData.od.sph || '');
              lensFormData.append('properties[OD CYL]', this.prescriptionData.od.cyl || '');
              lensFormData.append('properties[OD Axis]', this.prescriptionData.od.axis || '');
            }
            
            if (this.prescriptionData.os) {
              lensFormData.append('properties[OS SPH]', this.prescriptionData.os.sph || '');
              lensFormData.append('properties[OS CYL]', this.prescriptionData.os.cyl || '');
              lensFormData.append('properties[OS Axis]', this.prescriptionData.os.axis || '');
            }
            
            if (this.prescriptionData.pd) {
              lensFormData.append('properties[Left PD]', this.prescriptionData.pd.left || '');
              lensFormData.append('properties[Right PD]', this.prescriptionData.pd.right || '');
              lensFormData.append('properties[IPD]', this.prescriptionData.pd.ipd || '');
            }
            
            if (this.prescriptionData.addition) {
              lensFormData.append('properties[Addition]', this.prescriptionData.addition || '');
            }
            
            // Handle prescription image - upload as file if available, otherwise use data URL
            if (this.prescriptionData.imageFile) {
              // Upload as file - Shopify will handle it and return a /uploads/ URL
              lensFormData.append('properties[Prescription Image]', this.prescriptionData.imageFile);
            } else if (this.prescriptionData.image) {
              // Fallback to data URL if file object not available
              lensFormData.append('properties[Prescription Image]', this.prescriptionData.image);
            }
          }

          if (this.cart) {
            lensFormData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            lensFormData.append('sections_url', window.location.pathname);
          }

          const lensConfig = fetchConfig('javascript');
          lensConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete lensConfig.headers['Content-Type'];
          lensConfig.body = lensFormData;

          const lensResponse = await fetch(`${routes.cart_add_url}`, lensConfig);
          const lensResult = await lensResponse.json();

          if (lensResult.status) {
            this.handleErrorMessage(lensResult.description || 'Error adding lens to cart');
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            return;
          }

          // Success - use lens result as it has the latest cart state
          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: glassVariantId,
              cartData: lensResult,
            });
          }
          this.error = false;

          if (this.cart) {
            this.cart.renderContents(lensResult);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (e) {
          console.error('Error adding glasses with lens:', e);
          this.handleErrorMessage('An error occurred. Please try again.');
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}